// SETTINGS surface (N-FDE-1), recreated 1:1 from the prototype (screens.js settings): the sticky toolbar
// with the info affordance, and the two-column .set-grid of set-cards — the notify-me card and the
// value-free deployment inventory. The surface renders the kernel VALUE-FREE settings inventory (key /
// purpose / default / secret-flag — NEVER a runtime value, G-173). The notify-me settings are
// INFORMATIONAL: Ritona stores no per-user notify preference (BUILD-DEC-085 #6, Option A), so each row
// states its deployment DEFAULT posture and routes nothing — a deployment setting is changed in config by
// an Operator, not proposed from the dashboard (P-SELF, no fabricated control). Tokens only (G-181).

import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useProjection } from './useProjection.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { SettingsInventory, NotifyDefault } from './components/SettingsPanels.tsx';
import { ScreenToolbar, ScreenBody, TbInfo } from '../app/screen-chrome.tsx';
import { StatusPill } from './primitives.tsx';
import type { SettingRow, SettingsProjection, SetupReadinessItem } from './know-projections.ts';

/** True when a setting row is a notify-me setting (drives the notify list vs the general inventory). */
function isNotifyRow(row: SettingRow): boolean {
  const text = `${row.key} ${row.purpose}`.toLowerCase();
  return text.includes('notify') || text.includes('email me') || text.includes('alert');
}

/** The Settings surface. */
export function SettingsSurface(): React.JSX.Element {
  const api = useSurfaceApi();
  const state = useProjection<SettingsProjection>(api.getSettings);
  return (
    <>
      <ScreenToolbar title="Settings">
        <TbInfo
          title="Settings"
          tip="How Ritona reaches you, and this deployment's configuration — shown value-free. Tenant configuration is an Operator concern, changed in config, never here."
        />
      </ScreenToolbar>
      <ScreenBody>
        <SurfaceFrame state={state}>{(view) => <SettingsBody view={view} />}</SurfaceFrame>
      </ScreenBody>
    </>
  );
}

/** The Settings body — the prototype set-grid: the notify card + the value-free inventory cards. */
function SettingsBody({ view }: { readonly view: SettingsProjection }): React.JSX.Element {
  const notifyRows = view.settings.filter(isNotifyRow);
  const inventoryRows = view.settings.filter((row) => !isNotifyRow(row));
  return (
    <div>
      <SetupReadiness items={view.setupReadiness} />
      <div className="set-grid">
        <div className="set-col">
          <section aria-label="Notify me by email" className="set-card">
            <div className="h-section">Notify me by email when…</div>
            {notifyRows.length > 0 ? (
              <div className="notify-list" data-notify-list>
                {notifyRows.map((row) => (
                  <NotifyDefault
                    key={row.key}
                    settingKey={row.key}
                    title={row.purpose}
                    on={row.defaultText === 'true'}
                  />
                ))}
              </div>
            ) : (
              <p className="set-sub">No notify settings are declared for this deployment.</p>
            )}
            <p className="set-note">
              Ritona parks and notifies — it never stalls silently. A sustained failure always
              surfaces to a human.
            </p>
          </section>
        </div>

        <div className="set-col">
          <section aria-label="Deployment configuration">
            <SettingsInventory settings={inventoryRows} />
            <p className="set-note">
              These are deployment settings, configured per box and shown here for reference. Secret
              settings show their purpose, never a value.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

/** Runtime/provider readiness without exposing any configured value or credential. */
function SetupReadiness({
  items,
}: {
  readonly items: readonly SetupReadinessItem[];
}): React.JSX.Element {
  return (
    <section aria-label="Setup readiness" className="set-card mb-4">
      <div className="h-section">Setup readiness</div>
      <div className="space-y-3" data-setup-readiness>
        {items.map((item) => (
          // A configured box is deliberately amber until a real provider/runtime journey has succeeded.
          <div
            key={item.key}
            data-setup-key={item.key}
            data-setup-status={item.status}
            className="flex items-start justify-between gap-4 border-b border-border pb-3 last:border-0 last:pb-0"
          >
            <div>
              <div className="text-sm font-medium text-text">{item.title}</div>
              <div className="mt-0.5 text-sm text-text-subtle">{item.nextAction}</div>
            </div>
            <StatusPill tone={item.status === 'ready' ? 'go' : 'attention'}>
              {item.status === 'ready'
                ? 'Ready'
                : item.status === 'verification_required'
                  ? 'Test required'
                  : 'Needs setup'}
            </StatusPill>
          </div>
        ))}
      </div>
    </section>
  );
}
